import {Component, Input} from '@angular/core';

import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {PlanLodgementService} from '../../common/service/plan-lodgement.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {ATTACHMENT_CSD, ATTACHMENT_PLAN_PDF, PDF_CREATION_METHOD, PLAN_LODGEMENT_STAGE, PLAN_VALIDATION_STAGE} from '../../common/constants';
import {NgEventBus} from 'ng-event-bus';
import {TraitsService} from '../../common/service/traits.service';
import {AlertModalService, ConfirmationModalService, LodgementSuccessModalService, SettingsModalService} from '../../common/component/modal';
import {NotificationAlertsService} from '../../common/notification-alerts/notification-alerts.service';
import {filter, switchMap} from 'rxjs/operators';
import {PermissionsService} from '../../common/service/permissions.service';
import {catchError, EMPTY, map, Observable, of, Subscription, tap} from 'rxjs';
import {HamburgerService} from './hamburger.service';
import {NgForm} from '@angular/forms';
import {NlrPlanDetailService} from '../../plan-detail/plan-detail.service';
import {NLR_TABS as LeftComponentTabs} from '../../common/constants';
import {currentPlan} from '../../plan-dashboard/inner/inner-component.model';
import {NlrAssignPlanComponent} from '../../plan-dashboard/assign-plan/assign-plan.component';
import {NlrAssignPlanService} from '../../plan-dashboard/assign-plan/assign-plan.service';
import {SpatialUtilService} from '../../design/services/spatial-util.service';
import {MapService} from '../../design/services/map.service';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';
import {WindowWidgetsService} from '../../design/window-widgets/window-widgets.service';
import {DigitalPlanValidationStatusResponse} from '../../digital-plan/digital-plan.model';
import {DigitalPlanPrintModalService} from '../../digital-plan/print/digital-plan-print-modal.service';
import {DigitalPlanService} from '../../digital-plan/digital-plan.service';
import {PlanPdfService} from '../../common/service/plan-pdf.service';
import {DigitalPlanValidationState} from '../../digital-plan/digital-plan-validation-state';

@Component({
    selector: 'nlr-hamburger',
    imports: [NlrAssignPlanComponent],
    templateUrl: './hamburger.component.html',
    styleUrl: './hamburger.component.scss'
})
export class HamburgerComponent {
    @Input()
    planInformationForm: NgForm;

    @Input()
    analysisForm: NgForm;

    @Input()
    certificationForm: NgForm;

    @Input()
    interestsForm: NgForm;

    @Input()
    lodgePlanForm: NgForm;

    // Shared with the Digital Plan tab, which owns and displays the validation. The hamburger reads and triggers the
    // validation through this so both always reflect the same live state and reuse the same poll. Undefined until the
    // tab has been rendered at least once.
    @Input()
    digitalPlanValidationState: DigitalPlanValidationState;

    private get digitalPlanValidation(): DigitalPlanValidationStatusResponse {
        return this.digitalPlanValidationState?.validation;
    }

    private static readonly UNEXPECTED_ERROR_MESSAGE =
        'An unexpected error occurred. Please try again. If the problem persists contact Landgate Customer Services.';

    title = 'Hamburger Menu';

    isAssignPlanModalOpen = false;

    constructor(
        private permissionsService: PermissionsService,
        private alertModalService: AlertModalService,
        private confirmationModalService: ConfirmationModalService,
        private lodgementSuccessModalService: LodgementSuccessModalService,
        private eventBus: NgEventBus,
        private hamburgerService: HamburgerService,
        private nlrAssignPlanService: NlrAssignPlanService,
        private notificationAlertsService: NotificationAlertsService,
        private planDetailService: NlrPlanDetailService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private spatialUtilService: SpatialUtilService,
        private settingsModalService: SettingsModalService,
        private traitsService: TraitsService,
        private router: Router,
        private mapService: MapService,
        private windowWidgetsService: WindowWidgetsService,
        private digitalPlanPrintModalService: DigitalPlanPrintModalService,
        private digitalPlanService: DigitalPlanService,
        private planPdfService: PlanPdfService
    ) {}

    isHamburgerVisible() {
        return this.hamburgerService.isHamburgerVisible();
    }

    hideHamburger() {
        this.hamburgerService.hideHamburger();
    }

    displaySaveAction() {
        return this.planLodgementService.hasMinimumPlanDetails() && !this.planModelService.readonly;
    }

    createFromCad() {
        const planModel = this.getModel();

        if (planModel.pdfCreationMethod) {
            if (!this.planLodgementService.isCoordinateSystemValidForPdfCreationMethod(PDF_CREATION_METHOD.CAD_SOFTWARE)) {
                this.planLodgementService.showInvalidCoordinateSystemMessage(PDF_CREATION_METHOD.CAD_SOFTWARE);
                return;
            }

            // Captured before the conversion mutates pdfCreationMethod, so we know whether to cancel a digital plan validation below.
            const wasDigitalPlan = this.isDigitalPlan();

            // The Plan PDF the plan holds was produced from data it is about to stop holding, so it is deleted; the surveyor
            // attaches the one their CAD software produces instead.
            const confirmation: Observable<any> = this.hasPlanPDFAttachment()
                ? this.confirmationModalService
                      .showModal({
                          title: 'Warning',
                          confirmationText:
                              "Changing the 'Plan PDF creation method' will result in the deletion of the Plan PDF " +
                              'from the plan attachments! Do you wish to continue?'
                      })
                      .pipe(switchMap(action => (action === 'yes' ? of(null) : EMPTY)))
                : of(null);

            const subscription = confirmation
                .pipe(
                    tap(() => this.windowWidgetsService.closeAll()),
                    switchMap(() => {
                        this.planLodgementService.convertToCadPlan();

                        return this.planLodgementService.removePlanPDFAttachment() ? this.planLodgementService.updateAttachments(true) : of(null);
                    }),
                    tap(() => this.eventBus.cast('nlr:plan-model-updated:pdfCreationMethod')),
                    switchMap(() => (wasDigitalPlan ? this.planLodgementService.cancelDigitalPlanValidation() : of(null))),
                    tap(() => {
                        if (planModel.lodgementType && planModel.lodgementType.value === 'Amendment') {
                            planModel.planLots = 0;
                        }

                        this.eventBus.cast('nlr:selectTab', LeftComponentTabs.INFORMATION);

                        this.eventBus.cast('updatePlanLots');
                    })
                )
                .subscribe();
            this.eventBus.cast('nlr:busy', subscription);
        }

        this.hideHamburger();
    }

    createFromPortal() {
        const planModel = this.getModel();

        if (planModel.pdfCreationMethod) {
            if (!this.planLodgementService.isCoordinateSystemValidForPdfCreationMethod(PDF_CREATION_METHOD.SURVEYOR_PORTAL)) {
                this.planLodgementService.showInvalidCoordinateSystemMessage(PDF_CREATION_METHOD.SURVEYOR_PORTAL);
                return;
            }

            // Captured before the branches mutate pdfCreationMethod, so we know whether to cancel a digital plan validation below.
            const wasDigitalPlan = this.isDigitalPlan();

            let observable: Observable<any>;
            if (this.hasPlanPDFAttachment() || this.hasPlanCSDAttachment()) {
                observable = this.confirmationModalService
                    .showModal({
                        title: 'Warning',
                        confirmationText:
                            "Changing the 'Plan PDF creation method' will result in the deletion of the " +
                            'Plan PDF and CSD files from the plan attachments! Do you wish to continue?'
                    })
                    .pipe(
                        switchMap(action => {
                            if (action !== 'yes') {
                                return EMPTY;
                            }
                            planModel.pdfCreationMethod = PDF_CREATION_METHOD.SURVEYOR_PORTAL;
                            const pdfRemoved = this.planLodgementService.removePlanPDFAttachment();
                            const csdRemoved = this.planLodgementService.removePlanCSDAttachment();
                            return pdfRemoved || csdRemoved ? this.planLodgementService.updateAttachments(true) : of(null);
                        }),
                        tap(() => {
                            planModel.certification = {variationApprovals: []};
                            this.planLodgementService.deriveCertificationType();
                            this.planLodgementService.deriveRegulatoryStatement();
                            planModel.certification.variationApprovals = [];

                            this.eventBus.cast('updatePlanLots');

                            planModel.planHeading = undefined;

                            this.removeDigitalPlanAttachment();

                            this.eventBus.cast('nlr:plan-model-updated:pdfCreationMethod');
                        })
                    );
            } else {
                observable = new Observable(subscriber => {
                    planModel.pdfCreationMethod = PDF_CREATION_METHOD.SURVEYOR_PORTAL;

                    if (!planModel.certification) {
                        planModel.certification = {variationApprovals: []};
                    }

                    const certification = planModel.certification;
                    if (!certification.variationApprovals) {
                        certification.variationApprovals = [];
                    }

                    if (certification.variationApprovals.length === 0) {
                        certification.variationApprovals.push({variationType: null, regulatoryStatement: '', approvalReference: ''});
                    }

                    this.planLodgementService.deriveCertificationType();
                    this.planLodgementService.deriveRegulatoryStatement();

                    planModel.planHeading = undefined;

                    this.removeDigitalPlanAttachment();

                    this.eventBus.cast('nlr:plan-model-updated:pdfCreationMethod');
                    subscriber.next(null);
                    subscriber.complete();
                });
            }

            const subscription = observable
                .pipe(
                    switchMap(() => (wasDigitalPlan ? this.planLodgementService.cancelDigitalPlanValidation() : of(null))),
                    switchMap(() => {
                        this.planModelService.populateFormerTenureNotifications = true;

                        this.eventBus.cast('updatePlanLots');

                        this.eventBus.cast('nlr:selectTab', LeftComponentTabs.INFORMATION);

                        if (planModel.planPurpose) {
                            return this.traitsService.setPlanTraitsRequestData({planPurposeCode: planModel.planPurpose.id});
                        } else {
                            return of(null);
                        }
                    })
                )
                .subscribe();
            this.eventBus.cast('nlr:busy', subscription);
        }

        this.hideHamburger();
    }

    createDigitalPlan() {
        const planModel = this.getModel();

        if (planModel.pdfCreationMethod) {
            if (!this.planLodgementService.isCoordinateSystemValidForPdfCreationMethod(PDF_CREATION_METHOD.DIGITAL_PLAN)) {
                this.planLodgementService.showInvalidCoordinateSystemMessage(PDF_CREATION_METHOD.DIGITAL_PLAN);
                return;
            } else if (this.hasPlanPDFAttachment() || this.hasPlanCSDAttachment()) {
                this.confirmationModalService
                    .showModal({
                        title: 'Warning',
                        confirmationText:
                            "Changing the 'Plan PDF creation method' will result in the deletion of the " +
                            'Plan PDF and CSD files from the plan attachments! Do you wish to continue?'
                    })
                    .pipe(
                        switchMap(action => {
                            if (action !== 'yes') {
                                return EMPTY;
                            }
                            this.windowWidgetsService.closeAll();
                            const pdfRemoved = this.planLodgementService.removePlanPDFAttachment();
                            const csdRemoved = this.planLodgementService.removePlanCSDAttachment();
                            return pdfRemoved || csdRemoved ? this.planLodgementService.updateAttachments(true) : of(null);
                        }),
                        tap(() => {
                            planModel.pdfCreationMethod = PDF_CREATION_METHOD.DIGITAL_PLAN;
                            planModel.planLodgementStage = PLAN_LODGEMENT_STAGE.IN_DESIGN;
                            planModel.certification = {variationApprovals: []};
                            this.planLodgementService.deriveCertificationType();
                            this.planLodgementService.deriveRegulatoryStatement();

                            this.eventBus.cast('nlr:plan-model-updated:pdfCreationMethod');
                        })
                    )
                    .subscribe();
            } else {
                this.windowWidgetsService.closeAll();

                planModel.pdfCreationMethod = PDF_CREATION_METHOD.DIGITAL_PLAN;
                planModel.planLodgementStage = PLAN_LODGEMENT_STAGE.IN_DESIGN;

                // clean model attributes
                planModel.certification = {variationApprovals: []};
                this.planLodgementService.deriveCertificationType();
                this.planLodgementService.deriveRegulatoryStatement();
                planModel.interests = [];
                planModel.partiallySurrenderedInterests = undefined;

                planModel.formerTenures.forEach(formerTenure => (formerTenure.formerTenureNotifications = []));

                this.eventBus.cast('nlr:plan-model-updated:pdfCreationMethod');
            }

            if (planModel.lodgementType && planModel.lodgementType.value === 'Amendment') {
                planModel.planLots = 0;
            }

            this.eventBus.cast('nlr:selectTab', LeftComponentTabs.INFORMATION);

            this.eventBus.cast('updatePlanLots');
        }

        this.hideHamburger();
    }

    hasPlanPDFAttachment() {
        return this.getAttachments().some(attachment => attachment.attachmentType === ATTACHMENT_PLAN_PDF && attachment.fileLocation);
    }

    hasPlanCSDAttachment() {
        return this.getAttachments().some(attachment => attachment.attachmentType === ATTACHMENT_CSD && attachment.fileLocation);
    }

    isDisplayCreateFromCad() {
        return this.getModel().planPurpose?.pdfCreationMethods?.includes(PDF_CREATION_METHOD.CAD_SOFTWARE) && !this.isCadPlan();
    }

    isDisplayCreateFromPortal() {
        return this.getModel().planPurpose?.pdfCreationMethods?.includes(PDF_CREATION_METHOD.SURVEYOR_PORTAL) && !this.isPortalPlan();
    }

    isDisplayCreateDigitalPlan() {
        return this.getModel().planPurpose?.pdfCreationMethods?.includes(PDF_CREATION_METHOD.DIGITAL_PLAN) && !this.isDigitalPlan();
    }

    displayPlanActions() {
        return this.displaySaveAction();
    }

    displayPlanProcesses() {
        return this.planLodgementService.hasMinimumPlanDetails() && this.planModelService.getModel().id;
    }

    isInDesign() {
        return this.getModel().planLodgementStage === PLAN_LODGEMENT_STAGE.IN_DESIGN;
    }

    isReadyForQA() {
        return this.getModel().planLodgementStage === PLAN_LODGEMENT_STAGE.READY_FOR_QA;
    }

    isReadyForCertification() {
        return this.getModel().planLodgementStage === PLAN_LODGEMENT_STAGE.READY_FOR_CERTIFICATION;
    }

    isCertified() {
        return this.getModel().planLodgementStage === PLAN_LODGEMENT_STAGE.CERTIFIED;
    }

    isLodged() {
        return this.getModel().planLodgementStage === PLAN_LODGEMENT_STAGE.LODGED;
    }

    isPortalPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.SURVEYOR_PORTAL;
    }

    isCadPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.CAD_SOFTWARE;
    }

    isDigitalPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN;
    }

    isStatutoryPlan() {
        const id = this.getModel().planPurpose?.id;
        return id === 'FH_STAT' || id === 'CR_STAT';
    }

    showPrintPdfLink() {
        return this.isPortalPlan() && this.mapService.map && this.mapService.map.loaded && this.mapService.map.spatialReference;
    }

    openPrintPdfModal() {
        console.debug(`${this.title} component - openPrintPdfModal`);

        this.settingsModalService
            .showModal({
                spatialReference: this.mapService.map.spatialReference,
                // Restricting access based on permissions appears to be superfluous due of previous checks, but we do it anyway
                accessRestriction: this.planModelService.readonly || !this.permissionsService.hasEditPermission(['EDIT_PLAN'])
            })
            .subscribe();

        this.hideHamburger();
    }

    showPrintDigitalPlanPdfLink() {
        return (
            this.isDigitalPlan() &&
            this.digitalPlanValidation?.results?.some(result => result.type === 'Generate Plan PDF' && result.status === 'Success')
        );
    }

    printDigitalPlanPdf() {
        console.debug(`${this.title} component - printDigitalPlanPdf`);

        if (this.planPdfService.showWatermark(this.planModelService.getModel())) {
            this.digitalPlanPrintModalService.showModal().subscribe();
        } else {
            const subscription = this.digitalPlanService.printAndDisplay('').subscribe({
                error: error => {
                    console.error(`${this.title} - printDigitalPlanPdf - error:`, error);
                    this.notificationAlertsService.addMessage({text: 'Error printing plan'});
                }
            });
            this.eventBus.cast('nlr:busy', subscription);
        }

        this.hideHamburger();
    }

    triggerInDesign() {
        console.log(`${this.title} component - triggerInDesign`);

        let subscription: Subscription;

        if (this.hasPlanPDFAttachment()) {
            subscription = this.confirmationModalService
                .showModal({
                    title: 'Warning',
                    confirmationText:
                        "Changing the status of the plan to 'In Design' will void the attached plan " +
                        "pdf and it will be deleted from the plan. Selecting 'Yes' " +
                        'will delete the plan pdf from the list of attachments.'
                })
                .pipe(
                    switchMap(action => {
                        if (action !== 'yes') {
                            return EMPTY;
                        }
                        return this.planLodgementService.removePlanPDFAttachment() ? this.planLodgementService.updateAttachments(true) : of(null);
                    }),
                    switchMap(() => {
                        this.eventBus.cast('updatePlanLots');
                        return this.planModelService.save();
                    })
                )
                .subscribe(() => this.planStatusUpdatedModal('IN_DESIGN'));
        } else {
            subscription = this.planModelService.save().subscribe({
                next: () => this.planStatusUpdatedModal('IN_DESIGN'),
                complete: () => subscription.unsubscribe()
            });
        }

        this.eventBus.cast('nlr:busy', subscription);

        this.hideHamburger();
    }

    triggerRequestForQA() {
        console.log(`${this.title} component - triggerRequestForQA`);

        this.planLodgementService.increaseValidationAttempt();
        this.eventBus.cast('updatePlanLots');
        const subscription = this.planModelService.save().subscribe({
            next: () => {
                this.transitionStage(PLAN_LODGEMENT_STAGE.READY_FOR_QA);
            },
            error: error => this.alertModalService.createAlertMessage(this.toAlertMessage(error)),
            complete: () => subscription.unsubscribe()
        });
        this.eventBus.cast('nlr:busy', subscription);

        this.hideHamburger();
    }

    triggerRequestForCertification() {
        console.log(`${this.title} component - triggerRequestForCertification`);

        this.planLodgementService.increaseValidationAttempt();
        this.eventBus.cast('updatePlanLots');
        // Save via planLodgementService (not planModelService) so a plan change that makes an existing digital plan
        // validation stale cancels it before the partial re-validation below reuses it. The options keep this save
        // otherwise identical to the plain model save: no save-success alert, no spatial reconcile, no certified warning.
        const subscription = this.planLodgementService
            .save({showAlertOnSave: false, reconcileSpatialFeatures: false, showWarningForCertifiedPlan: false})
            .subscribe({
                next: () => {
                    this.transitionStage(PLAN_LODGEMENT_STAGE.READY_FOR_CERTIFICATION);
                },
                error: error => this.alertModalService.createAlertMessage(this.toAlertMessage(error)),
                complete: () => subscription.unsubscribe()
            });
        this.eventBus.cast('nlr:busy', subscription);

        this.hideHamburger();
    }

    triggerCertification() {
        console.log(`${this.title} component - triggerCertification`);

        this.planLodgementService.increaseValidationAttempt();
        this.eventBus.cast('updatePlanLots');
        const subscription = this.planModelService.save().subscribe({
            next: () => {
                this.transitionStage(PLAN_LODGEMENT_STAGE.CERTIFIED);
            },
            error: error => this.alertModalService.createAlertMessage(this.toAlertMessage(error)),
            complete: () => subscription.unsubscribe()
        });
        this.eventBus.cast('nlr:busy', subscription);

        this.hideHamburger();
    }

    triggerLodge() {
        console.log(`${this.title} component - triggerLodge`);

        this.planLodgementService.increaseValidationAttempt();
        // This part causes a malfunction for Total plan lots. Comment out as it seems unneccessary.
        // TODO: If code below is neccessary, it needs to be diffrent way
        // this.eventBus.cast('updatePlanLots');

        const subscription = this.planLodgementService.save({showAlertOnSave: false, showWarningForCertifiedPlan: false}).subscribe({
            next: () => {
                this.transitionStage(PLAN_LODGEMENT_STAGE.LODGED);
            },
            complete: () => subscription.unsubscribe()
        });

        this.eventBus.cast('nlr:busy', subscription);

        this.hideHamburger();
    }

    transitionStage(lodgementStage: string) {
        console.debug(`${this.title} component - transitionStage`);

        this.validateFormsForTransition();

        if (lodgementStage === PLAN_LODGEMENT_STAGE.LODGED) {
            this.lodgePlan(lodgementStage);
        } else if (lodgementStage === PLAN_LODGEMENT_STAGE.CERTIFIED) {
            this.certifyPlan(lodgementStage);
        } else {
            this.transitionToStage(lodgementStage);
        }
    }

    /** Submits and validates the shared plan forms, queuing a notification for each page that is incomplete or invalid. */
    private validateFormsForTransition() {
        this.planInformationForm.onSubmit(undefined);
        if (this.planInformationForm.invalid) {
            if (!this.notificationAlertsService.messages.some(message => message.subType === 'InformationForm')) {
                this.notificationAlertsService.addMessage({
                    subType: 'InformationForm',
                    text: 'The Plan Information page is incomplete or data entered is invalid!'
                });
            }
        }

        if (this.analysisForm) {
            this.analysisForm.onSubmit(undefined);
            if (this.analysisForm.invalid) {
                if (!this.notificationAlertsService.messages.some(message => message.subType === 'AnalysisForm')) {
                    this.notificationAlertsService.addMessage({
                        subType: 'AnalysisForm',
                        text: 'The Analysis page is incomplete or data entered is invalid!'
                    });
                }
            }
        }

        if (this.planLodgementService.interestsApplicable()) {
            this.interestsForm.onSubmit(undefined);
            if (this.interestsForm.invalid) {
                if (!this.notificationAlertsService.messages.some(message => message.subType === 'InterestForm')) {
                    this.notificationAlertsService.addMessage({
                        subType: 'InterestForm',
                        text: 'The Interests page is incomplete or data entered is invalid!'
                    });
                }
            }
        }

        if (this.planLodgementService.certificationApplicable()) {
            this.certificationForm.onSubmit(undefined);
            if (this.certificationForm.invalid) {
                if (!this.notificationAlertsService.messages.some(message => message.subType === 'CertificationForm')) {
                    this.notificationAlertsService.addMessage({
                        subType: 'CertificationForm',
                        text: 'The Certification page is incomplete or data entered is invalid!'
                    });
                }
            }
        }
    }

    private lodgePlan(lodgementStage: string) {
        this.lodgePlanForm.onSubmit(undefined);
        if (this.lodgePlanForm.invalid) {
            if (!this.notificationAlertsService.messages.some(message => message.subType === 'LodgeForm')) {
                this.notificationAlertsService.addMessage({
                    subType: 'LodgeForm',
                    text: 'The Lodge page is incomplete or data entered is invalid!'
                });
            }
        }

        if (this.getAttachments().some(attachment => attachment.optionality === 'Prohibited')) {
            this.alertModalService.createAlertMessage('Prohibited attachments must be removed before lodging.');
            return;
        }

        const subscription = this.runValidationForStages(this.getApplicableValidationStages(lodgementStage))
            .pipe(
                filter(() => {
                    return (
                        (this.planInformationForm.disabled || this.planInformationForm.valid) &&
                        this.lodgePlanForm.valid &&
                        (!this.planLodgementService.analysisApplicable() || this.analysisForm.disabled || this.analysisForm.valid) &&
                        (!this.planLodgementService.certificationApplicable() || this.certificationForm.disabled || this.certificationForm.valid) &&
                        (!this.planLodgementService.interestsApplicable() || this.interestsForm.disabled || this.interestsForm.valid) &&
                        (!this.isDigitalPlan() || this.digitalPlanValidation?.status === 'Success') &&
                        !this.planDetailService.hasValidationErrors()
                    );
                }),
                switchMap(() => this.planModelService.lodge()),
                tap(() => this.showLodgementSuccessModal()),
                catchError(reason => {
                    console.error(`${this.title} - transition error:`, reason);
                    this.alertModalService.createAlertMessage(this.toAlertMessage(reason));
                    return EMPTY; // Stop stream
                })
            )
            .subscribe({complete: () => subscription.unsubscribe()});

        this.eventBus.cast('nlr:busy', subscription);
    }

    private certifyPlan(lodgementStage: string) {
        const subscription = this.runValidationForStages(this.getApplicableValidationStages(lodgementStage))
            .pipe(
                filter(() => {
                    return (
                        (this.planInformationForm.disabled || this.planInformationForm.valid) &&
                        (!this.planLodgementService.analysisApplicable() || this.analysisForm.disabled || this.analysisForm.valid) &&
                        (!this.planLodgementService.certificationApplicable() || this.certificationForm.disabled || this.certificationForm.valid) &&
                        (!this.planLodgementService.interestsApplicable() || this.interestsForm.disabled || this.interestsForm.valid) &&
                        (!this.isDigitalPlan() || this.digitalPlanValidation?.status === 'Success') &&
                        !this.planDetailService.hasValidationErrorsExcludingLodgeTab()
                    );
                }),
                map(() => this.planStatusUpdatedModal(lodgementStage)),
                catchError(reason => {
                    console.error(`${this.title} - transition error:`, reason);
                    this.alertModalService.createAlertMessage(this.toAlertMessage(reason));
                    return EMPTY; // Stop stream
                })
            )
            .subscribe({complete: () => subscription.unsubscribe()});

        this.eventBus.cast('nlr:busy', subscription);
    }

    private transitionToStage(lodgementStage: string) {
        const subscription = this.runValidationForStages(this.getApplicableValidationStages(lodgementStage))
            .pipe(
                // The other page/rule validations must succeed before we spend time on the digital plan validation. If they
                // fail, short-circuit: the transition is going to be blocked anyway, and there is no point running the
                // (comparatively expensive) digital plan validation.
                switchMap(() => {
                    if (!this.otherValidationsSucceededForTransition()) {
                        return of(false);
                    }

                    // A digital plan re-runs its own validation while progressing to Ready for Certification, and the
                    // transition is blocked unless it succeeds. Statutory plans don't re-run it here; the upload validation
                    // is sufficient. Any other stage transition (e.g. reverting to In Design to fix errors) is never gated.
                    if (this.isDigitalPlan() && !this.isStatutoryPlan() && lodgementStage === PLAN_LODGEMENT_STAGE.READY_FOR_CERTIFICATION) {
                        // Switch to the Digital Plan tab before triggering the validation so its live progress is visible
                        // while the (comparatively expensive) validation runs, not just once it has failed.
                        this.eventBus.cast('nlr:selectTab', LeftComponentTabs.DIGITAL_PLAN);

                        // Run through the shared validation state so the run reuses the tab's subscription and displayed
                        // state (owned by the tab and torn down with it, and showing live progress while this runs).
                        // Reuse the existing full validation and re-run only the FME status=LODGE validation subset; the
                        // backend falls back to a full validation when there is no prior validation to reuse, or any of
                        // the reused (non-partial) jobs did not previously succeed.
                        return this.digitalPlanValidationState.validate(true).pipe(
                            switchMap(digitalPlanValidation => {
                                if (digitalPlanValidation?.status !== 'Success') {
                                    // The transition is blocked by digital plan validation errors; the Digital Plan tab is
                                    // already selected (see above), so the surfaced errors are visible.
                                    if (!this.notificationAlertsService.messages.some(message => message.subType === 'DigitalPlanForm')) {
                                        this.notificationAlertsService.addMessage({
                                            subType: 'DigitalPlanForm',
                                            text: 'The Digital Plan page is incomplete or data entered is invalid!'
                                        });
                                    }
                                    return of(false);
                                }
                                // A successful validation updates planLots/chargeablePlanLots (derived from the CSD job).
                                // Derive them straight from the completed validation before saving, rather than relying on
                                // the Digital Plan tab's change-detection tick to have already written them onto the model:
                                // during the re-run the in-progress polls carry no CSD lot count, so the last value the tab
                                // wrote is 0, and save() runs synchronously on validation completion before the next tick
                                // can re-derive them. Persist here so the counts saved reflect the completed validation and
                                // the transition doesn't warn about (and discard) unsaved counts.
                                this.digitalPlanValidationState.applyLotCounts(this.planModelService.getModel());
                                return this.planModelService.save().pipe(map(() => true));
                            })
                        );
                    }

                    return of(true);
                }),
                filter(canTransition => canTransition),
                map(() => this.planStatusUpdatedModal(lodgementStage)),
                catchError(reason => {
                    console.error(`${this.title} - transition error:`, reason);
                    this.alertModalService.createAlertMessage(this.toAlertMessage(reason));
                    return EMPTY; // Stop stream
                })
            )
            .subscribe({complete: () => subscription.unsubscribe()});

        this.eventBus.cast('nlr:busy', subscription);
    }

    /** Whether the shared page/rule validations (excluding the digital plan validation) permit a stage transition. */
    private otherValidationsSucceededForTransition(): boolean {
        return (
            (this.planInformationForm.disabled || this.planInformationForm.valid) &&
            (!this.planLodgementService.analysisApplicable() || this.analysisForm.disabled || this.analysisForm.valid) &&
            (!this.planLodgementService.interestsApplicable() || this.interestsForm.disabled || this.interestsForm.valid) &&
            !this.planDetailService.hasValidationErrorsExcludingLodgeTab()
        );
    }

    private getApplicableValidationStages(lodgementStage: string) {
        const stages = [
            PLAN_VALIDATION_STAGE.LODGEMENT_INFORMATION,
            PLAN_VALIDATION_STAGE.LODGEMENT_ANALYSIS,
            PLAN_VALIDATION_STAGE.LODGEMENT_CERTIFICATION
        ];

        if (this.planLodgementService.interestsApplicable()) {
            stages.push(PLAN_VALIDATION_STAGE.LODGEMENT_INTERESTS);
        }

        if (this.planLodgementService.designApplicable()) {
            stages.push(PLAN_VALIDATION_STAGE.LODGEMENT_DESIGN);
        }

        if (lodgementStage === PLAN_LODGEMENT_STAGE.LODGED) {
            stages.push(PLAN_VALIDATION_STAGE.LODGEMENT_LODGE);
        }

        return stages;
    }

    private runValidationForStages(stages: string[]) {
        return forkJoinDefaultIfEmpty(stages.map((stage: string) => this.planDetailService.runValidationsForStage(stage)));
    }

    /**
     * Extracts a human-readable message from a caught error. For an HttpErrorResponse the backend response body is held in
     * `error`; falling back to `message` avoids surfacing the raw error object as '[object Object]' in the alert modal.
     */
    private toAlertMessage(reason: unknown): string | string[] {
        const body = reason instanceof HttpErrorResponse ? reason.error : reason;
        if (typeof body === 'string' || Array.isArray(body)) {
            return body;
        }
        if (typeof (body as {message?: unknown})?.message === 'string') {
            return (body as {message: string}).message;
        }
        if (reason instanceof Error) {
            return reason.message;
        }
        return HamburgerComponent.UNEXPECTED_ERROR_MESSAGE;
    }

    private showLodgementSuccessModal() {
        this.lodgementSuccessModalService.showModal().subscribe(() => this.router.navigate(['/plan-dashboard']));
    }

    showInDesign() {
        return (
            this.permissionsService.hasEditPermission(['EDIT_PLAN'], this.getModel().firm?.firmNumber) &&
            this.displayPlanProcesses() &&
            (this.isPortalPlan() || this.isDigitalPlan()) &&
            (this.isReadyForQA() || this.isReadyForCertification() || this.isCertified())
        );
    }

    showReadyForQA() {
        return (
            this.permissionsService.hasEditPermission(['REQUEST_QA'], this.getModel().firm?.firmNumber) &&
            this.displayPlanProcesses() &&
            this.isPortalPlan() &&
            this.isInDesign()
        );
    }

    showReadyForCertification() {
        return (
            this.permissionsService.hasEditPermission(['REQUEST_CERTIFICATION'], this.getModel().firm?.firmNumber) &&
            this.displayPlanProcesses() &&
            (this.isPortalPlan() || this.isDigitalPlan()) &&
            (this.isReadyForQA() || this.isInDesign())
        );
    }

    showReadyForLodgement() {
        return (
            this.permissionsService.hasEditPermission(['CERTIFY_PLAN'], this.getModel().firm?.firmNumber) &&
            this.displayPlanProcesses() &&
            (this.isPortalPlan() || this.isDigitalPlan()) &&
            this.isReadyForCertification()
        );
    }

    showLodgePlan() {
        return (
            this.permissionsService.hasEditPermission(['LODGE_PLAN'], this.getModel().firm?.firmNumber) &&
            this.displayPlanProcesses() &&
            (this.isCadPlan() || this.isCertified()) &&
            !this.isLodged()
        );
    }

    showNoAvailableProcesses() {
        return !this.permissionsService.hasEditPermission(
            ['CERTIFY_PLAN', 'LODGE_PLAN', 'REQUEST_CERTIFICATION', 'REQUEST_QA'],
            this.getModel().firm?.firmNumber
        );
    }

    save() {
        console.debug(`${this.title} component - save`);

        const subscription = (this.spatialUtilService.hasEdits() ? this.spatialUtilService.save() : of(null))
            .pipe(switchMap(() => this.planLodgementService.save({showWarningForCertifiedPlan: false})))
            .subscribe({complete: () => subscription.unsubscribe()});

        this.eventBus.cast('nlr:busy', subscription);

        this.hideHamburger();
    }

    /**
     * Transitions the plan to the provided stage. e.g when moving from IN_DESIGN to READY_FOR_QA
     * @param stage the stage to transition to
     * @private
     */
    private planStatusUpdatedModal(stage: string) {
        console.debug(`${this.title} - planStatusUpdatedModal`);

        const subscription = this.planLodgementService
            .transitionStage(stage)
            .pipe(
                switchMap(() =>
                    this.confirmationModalService.showModal({
                        title: 'Plan status updated',
                        confirmationText: 'The plan status has been updated. Do you want to re-assign the plan?',
                        iconType: 'info'
                    })
                )
            )
            .subscribe({
                next: action => {
                    if (action === 'yes') {
                        this.showAssignPlanModal();
                    }
                },
                error: error => {
                    if (error) {
                        this.alertModalService.createAlertMessage(this.toAlertMessage(error));
                    }
                },
                complete: () => subscription.unsubscribe()
            });

        this.eventBus.cast('nlr:busy', subscription);
    }

    private showAssignPlanModal() {
        const planModel = this.getModel();
        const planSummary = {
            assignedTo: planModel.assignedTo,
            assignedToName: planModel.assignedToName,
            certifiedBy: undefined,
            certifiedByName: undefined,
            createdDate: undefined,
            creator: planModel.creator,
            creatorName: planModel.creatorName,
            customerReference: undefined,
            pdfCreationMethod: undefined,
            description: planModel.planHeading,
            planLodgementId: planModel.id,
            planLodgementStage: planModel.planLodgementStage,
            planLodgementType: planModel.lodgementType.value,
            planNumber: planModel.planNumber,
            planPurpose: planModel.planPurpose?.name,
            planType: planModel.planType.id,
            requisitionDate: undefined,
            stage: 1,
            statusDate: undefined,
            statusDescription: undefined,
            surveyor: planModel.surveyor,
            surveyorFirm: planModel.firm.firmNumber,
            surveyorFirmName: planModel.firm.firmName,
            tenureType: planModel.tenureType?.name,
            version: planModel.versionNumber,
            certifyingSurveyor: undefined
        } as currentPlan;

        this.nlrAssignPlanService.updatePlanSummary(planSummary);
        this.isAssignPlanModalOpen = true;
    }

    closeAssignPlanModal() {
        this.isAssignPlanModalOpen = false;
    }

    assignPlan(plan: currentPlan) {
        this.planModelService.assign(plan.assignedTo, plan.assignedToName, plan.planLodgementId).subscribe({
            next: () => {
                console.debug(`${this.title} - assign plan success`);
                this.alertModalService.createAlertMessage('The plan has been assigned successfully.', 'Ok', 'Plan assigned', 'success');
                this.hideHamburger();
                this.router.navigate(['/plan-dashboard']);
            },
            error: reason => {
                console.error(`${this.title} - assign plan error:`, reason);
                this.alertModalService.createAlertMessage(this.toAlertMessage(reason));
            }
        });
    }

    private getAttachments() {
        return this.getModel().attachments || [];
    }

    private getModel() {
        return this.planModelService.getModel();
    }

    private removeDigitalPlanAttachment() {
        const planModel = this.planModelService.getModel();
        if (planModel.digitalPlanAttachment) {
            delete planModel.digitalPlanAttachment;
        }
    }
}
